import React from 'react';
import * as Sentry from '@sentry/react';
import { AuthGuard, PageLayout, RootComponent } from 'components';
import {
  HomePage,
  MyCampaigns,
  CampaignCreationSuccessPage,
  Contact,
  LogoutPage,
  LoginPage,
  Ga4Page,
  ErrorPage,
  TermsAndConditionsPage,
  PrivacyPolicyPage,
  ResetPassword,
  Profile,
  EditAddress,
  InvoicesPage,
  Overview,
  CampaignCreatePage,
  EditUserGa4,
} from 'pages';
import { RouteList, UserAddressType } from 'types';
import { createBrowserRouter, RouteObject } from 'react-router-dom';
import { adminRoutes } from './adminRoutes';

/**
 * Route configuration with a single root route structure:
 * - Single errorElement as catch-all for rendering/lifecycle errors
 * - RootComponent provides common layout without auth dependency
 * - AuthGuard wraps protected routes requiring authentication
 * - Public routes (login, logout, etc.) are accessible without auth
 * - Catch-all '*' route handles unknown paths
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootComponent />,
    errorElement: <ErrorPage />,
    children: [
      // ==========================================
      // Public routes (no authentication required)
      // ==========================================
      {
        path: RouteList.Login,
        element: <LoginPage />,
      },
      {
        path: RouteList.Logout,
        element: <LogoutPage />,
      },
      {
        path: RouteList.ResetPassword,
        element: <ResetPassword />,
      },
      {
        path: RouteList.Ga4Config,
        element: <Ga4Page />,
      },
      {
        // Public pages with layout
        element: <PageLayout />,
        children: [
          {
            path: RouteList.TermsAndConditions,
            element: <TermsAndConditionsPage />,
          },
          {
            path: RouteList.PrivacyPolicy,
            element: <PrivacyPolicyPage />,
          },
        ],
      },

      // ==========================================
      // Protected routes (authentication required)
      // ==========================================
      {
        element: <AuthGuard />,
        children: [
          {
            element: <PageLayout />,
            children: [
              {
                path: RouteList.Home,
                element: <MyCampaigns />,
              },
              {
                path: RouteList.Campaign,
                element: <HomePage />,
              },
              {
                path: RouteList.CampaignCreate,
                element: <CampaignCreatePage />,
              },
              {
                path: RouteList.CampaignOverview,
                element: <Overview />,
              },
              {
                path: RouteList.MyCampaigns,
                element: <MyCampaigns />,
              },
              {
                path: RouteList.CampaignCreationSuccess,
                element: <CampaignCreationSuccessPage />,
              },
              {
                path: RouteList.Contact,
                element: <Contact />,
              },
              {
                path: RouteList.Profile,
                element: <Profile />,
              },
              {
                path: RouteList.EditUserGa4,
                element: <EditUserGa4 />,
              },
              {
                path: RouteList.EditContactAddress,
                element: <EditAddress formType={UserAddressType.contact} />,
              },
              {
                path: RouteList.EditBillingAddress,
                element: <EditAddress formType={UserAddressType.billing} />,
              },
              {
                path: RouteList.Invoices,
                element: <InvoicesPage />,
              },
              // Admin routes
              ...adminRoutes,
            ],
          },
        ],
      },

      // ==========================================
      // Catch-all route for unknown paths (404)
      // ==========================================
      {
        path: '*',
        element: <ErrorPage />,
      },
    ],
  },
];

export const routerFutureFlags = {
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_prependBasename: true,
  v7_relativeSplatPath: true,
  v7_skipActionErrorRevalidation: true,
  v7_skipActionStatusRevalidation: true,
  v7_startTransition: true,
};

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routes, { future: routerFutureFlags });
